import { Alert, Button, Content } from "@patternfly/react-core";
import { Modal, ModalVariant } from "@patternfly/react-core/deprecated";
import { ErrorPopup, useAlertContext } from "@rhc-shared-components/alert-component";
import { FormContainer } from "@rhc-shared-components/form";
import { FormTextInput } from "@rhc-shared-components/form-text-input";
import { useQueryClient } from "@tanstack/react-query";
import { Formik } from "formik";
import { isEmpty } from "lodash";
import { type FC, useCallback, useEffect, useRef, useState } from "react";
import { useParams } from "react-router";
import * as Yup from "yup";
import { AIExtractError, createExtractStream, extractProductFields } from "../../../api/connectAI";
import { patchProductListing } from "../../../api/pyxis";
import AIImportIcon from "../../../assets/ai-import-icon.svg?react";
import type { ProductRouteParams } from "../../../constants/routerconstants";
import { useProductMetadata } from "../../../hooks/useProductMetadata";
import type { IPyxisAPIError } from "../../../interfaces/pyxis";
import { aiRateLimitQueryKey } from "../../../queries/connectAI";
import { invalidateProductQueries, productDetailQueryKey } from "../../../queries/productDetails";
import type { ProductListing } from "../../../types/generated-graphql";
import { MakeEmptyFieldNull } from "../../../utils/emptyToNull";
import type { SSEStream } from "../../../utils/sse-stream";
import AIFieldReviewBody from "./AIFieldReview";
import { buildPatchPayload, buildReviewFields } from "./fieldMapping";
import GeneratingView from "./GeneratingView";
import type { EditMode, FieldValue, ModalView, ReviewField } from "./types";
import "./AIImportModal.scss";

export interface AIImportModalProps {
	isOpen: boolean;
	onClose: () => void;
}

// ─── URL form ────────────────────────────────────────────────────────

const MODAL_TITLE = "Import product fields from website";

const PROTOCOL_REGEX = /^https?:\/\//i;
const URL_REGEX = /^https?:\/\/\S+\.\S{2,}$/i;

const parseUrlEntries = (value: string): string[] =>
	value
		.split(",")
		.map((u) => u.trim())
		.filter(Boolean);

const urlValidationSchema = Yup.object().shape({
	urls: Yup.string()
		.required("This field is required")
		.test("has-protocol", "All URLs must start with http:// or https://", (value) => {
			if (!value) return false;
			return parseUrlEntries(value).every((u) => PROTOCOL_REGEX.test(u));
		})
		.test("valid-urls", "Please enter valid URL(s)", (value) => {
			if (!value) return false;
			const urls = parseUrlEntries(value);
			return urls.length > 0 && urls.every((u) => URL_REGEX.test(u));
		}),
});

const EMPTY_ARRAY_ITEMS: Record<string, Record<string, string>> = {
	features: { title: "", description: "" },
	faqs: { question: "", answer: "" },
	linked_resources: { type: "", title: "", description: "", url: "", category: "documentation & support" },
	contacts: { email_address: "", type: "Marketing contact" },
	search_aliases: { key: "", value: "" },
};

// ─── Component ───────────────────────────────────────────────────────

const AIImportModal: FC<AIImportModalProps> = ({ isOpen, onClose }) => {
	const [view, setView] = useState<ModalView>("form");
	const [submittedUrls, setSubmittedUrls] = useState<string[]>([]);
	const [reviewFields, setReviewFields] = useState<ReviewField[]>([]);
	const [currentIndex, setCurrentIndex] = useState(0);
	const [isSaving, setIsSaving] = useState(false);
	const [showQuitWarning, setShowQuitWarning] = useState(false);
	const [extractError, setExtractError] = useState<string>("");
	const [statusMessage, setStatusMessage] = useState<{ text: string; variant: "success" | "info" }>({
		text: "",
		variant: "success",
	});
	const [editMode, setEditMode] = useState<EditMode>("none");
	const [generationId, setGenerationId] = useState("");

	const hasSavedFieldsRef = useRef(false);
	const aiGeneratedFlagSentRef = useRef(false);
	const streamRef = useRef<SSEStream | null>(null);
	const { productDetail } = useProductMetadata();
	const { setAlertState } = useAlertContext();
	const queryClient = useQueryClient();
	const params = useParams<ProductRouteParams>();
	const currentField = reviewFields[currentIndex];
	const pendingFields = reviewFields.filter((f) => f.status === "pending");

	const resetState = useCallback(() => {
		if (streamRef.current) {
			streamRef.current.abort();
			streamRef.current = null;
		}
		setView("form");
		setSubmittedUrls([]);
		setReviewFields([]);
		setCurrentIndex(0);
		setIsSaving(false);
		setShowQuitWarning(false);
		setExtractError("");
		setEditMode("none");
		setGenerationId("");
	}, []);

	useEffect(() => {
		if (isOpen) {
			document.body.classList.add("stop-scrolling");
		} else {
			document.body.classList.remove("stop-scrolling");
			resetState();
		}
	}, [isOpen, resetState]);

	const handleClose = useCallback(() => {
		resetState();
		if (hasSavedFieldsRef.current) {
			hasSavedFieldsRef.current = false;
			void queryClient.invalidateQueries({ queryKey: productDetailQueryKey(params.productId) });
			void invalidateProductQueries(queryClient, params.productId);
		}
		aiGeneratedFlagSentRef.current = false;
		onClose();
	}, [onClose, queryClient, params.productId, resetState]);

	const handleGenerate = useCallback(
		async (urls: string[]) => {
			setSubmittedUrls(urls);
			setView("generating");
			const stream = createExtractStream(urls);
			streamRef.current = stream;
			try {
				const { data: aiData, requestId } = await extractProductFields(stream);
				void queryClient.invalidateQueries({ queryKey: aiRateLimitQueryKey });
				setGenerationId(requestId);
				const fields = buildReviewFields(aiData, productDetail);
				if (fields.length === 0) {
					ErrorPopup(setAlertState, "No fields could be extracted from the provided URL(s)");
					handleClose();
					return;
				}
				setReviewFields(fields);
				setCurrentIndex(0);
				setStatusMessage({ text: `${fields.length} fields successfully generated.`, variant: "success" });
				setView("reviewing");
			} catch (error: unknown) {
				if ((error as Error).name === "AbortError") return;
				void queryClient.invalidateQueries({ queryKey: aiRateLimitQueryKey });
				if (error instanceof AIExtractError && error.status >= 500) {
					setExtractError(error.message);
					setView("error");
					return;
				}
				// 400 (URL not crawlable) or other client errors — go back to form
				ErrorPopup(setAlertState, (error as Error).message || "Failed to generate product fields");
				setView("form");
			}
		},
		[productDetail, setAlertState, handleClose, queryClient],
	);

	const handleSaveField = async (value: FieldValue) => {
		if (!currentField) return;
		setIsSaving(true);
		try {
			const patchData = buildPatchPayload(currentField.pyxisPath, value);
			const sanitized = MakeEmptyFieldNull(patchData);
			const payload: Partial<ProductListing> = {
				_id: params.productId,
				...sanitized,
				...(editMode === "ai" &&
					!productDetail.ai_generated &&
					!aiGeneratedFlagSentRef.current && { ai_generated: true }),
			};
			await patchProductListing(payload);
			if (editMode === "ai") aiGeneratedFlagSentRef.current = true;
			hasSavedFieldsRef.current = true;

			const updated = [...reviewFields];
			updated[currentIndex] = { ...updated[currentIndex], status: "saved", existingValue: value, hasConflict: true };
			setReviewFields(updated);
			setStatusMessage({ text: `${currentField.label} field saved`, variant: "success" });

			const allResolved = updated.every((f) => f.status !== "pending");
			if (allResolved) {
				setView("complete");
				return;
			}

			setEditMode("none");
			if (currentIndex + 1 < updated.length) setCurrentIndex(currentIndex + 1);
			else {
				const firstPending = updated.findIndex((f) => f.status === "pending");
				if (firstPending !== -1) setCurrentIndex(firstPending);
				else setView("complete");
			}
		} catch (error: unknown) {
			ErrorPopup(setAlertState, (error as IPyxisAPIError)?.detail || "Failed to save field");
		} finally {
			setIsSaving(false);
		}
	};

	const handleSkip = () => {
		const updated = [...reviewFields];
		updated[currentIndex] = { ...updated[currentIndex], status: "skipped" };
		setReviewFields(updated);
		setEditMode("none");

		const allResolved = updated.every((f) => f.status !== "pending");
		if (allResolved) {
			if (hasSavedFieldsRef.current) {
				setView("complete");
			} else {
				handleClose();
			}
			return;
		}

		if (currentIndex + 1 < updated.length) setCurrentIndex(currentIndex + 1);
	};

	const handleReject = () => {
		handleSkip();
	};

	const handleCloseAttempt = () => {
		if (pendingFields.length > 0) setShowQuitWarning(true);
		else handleClose();
	};

	const handleContinueReviewing = () => {
		setShowQuitWarning(false);
		setEditMode("none");
		const firstPending = reviewFields.findIndex((f) => f.status === "pending");
		if (firstPending !== -1) setCurrentIndex(firstPending);
	};

	const findPreviousPending = useCallback(() => {
		for (let i = currentIndex - 1; i >= 0; i--) {
			if (reviewFields[i].status === "pending") return i;
		}
		return -1;
	}, [currentIndex, reviewFields]);

	const findNextPending = useCallback(() => {
		for (let i = currentIndex + 1; i < reviewFields.length; i++) {
			if (reviewFields[i].status === "pending") return i;
		}
		return -1;
	}, [currentIndex, reviewFields]);

	const handlePrevious = useCallback(() => {
		const prev = findPreviousPending();
		if (prev !== -1) {
			setEditMode("none");
			setCurrentIndex(prev);
		}
	}, [findPreviousPending]);

	const handleNext = useCallback(() => {
		const next = findNextPending();
		if (next !== -1) {
			setEditMode("none");
			setCurrentIndex(next);
		}
	}, [findNextPending]);

	if (!isOpen) return null;

	const modalHeader = (
		<div className="ai-import-modal__header">
			<AIImportIcon />
			<span>{MODAL_TITLE}</span>
		</div>
	);

	// ─── Generating ──────────────────────────────────────────────────
	if (view === "generating") {
		return (
			<Modal
				variant={ModalVariant.medium}
				isOpen={isOpen}
				onClose={handleClose}
				appendTo={document.getElementById("modal-div-container") ?? document.body}
				id="ai-import-modal"
				className="ai-import-modal"
				aria-label={MODAL_TITLE}
				header={modalHeader}
				actions={[
					<Button key="cancel" variant="link" isInline={true} onClick={handleClose}>
						Cancel
					</Button>,
				]}
			>
				<GeneratingView />
			</Modal>
		);
	}

	// ─── Error (server-side 500, e.g. Gemini unavailable) ──────────────
	if (view === "error") {
		return (
			<Modal
				variant={ModalVariant.medium}
				isOpen={isOpen}
				onClose={handleClose}
				appendTo={document.getElementById("modal-div-container") ?? document.body}
				id="ai-import-modal"
				className="ai-import-modal"
				aria-label={MODAL_TITLE}
				header={modalHeader}
				actions={[
					<Button key="retry" variant="primary" onClick={() => handleGenerate(submittedUrls)}>
						Try again
					</Button>,
					<Button key="cancel" variant="link" onClick={handleClose}>
						Cancel
					</Button>,
				]}
			>
				<Alert variant="danger" isInline title="Failed to generate product fields">
					{extractError}
				</Alert>
			</Modal>
		);
	}

	// ─── Review (Formik wraps Modal → footer has access to formikProps) ──
	if (view === "reviewing" && currentField) {
		const getValidationSchema = () => {
			if (!currentField.validationSchema) return Yup.object().shape({ fieldValue: Yup.mixed() });
			return Yup.object().shape({ fieldValue: currentField.validationSchema as Yup.Schema<unknown> });
		};

		const getInitialValue = (): FieldValue => {
			if (currentField.inputType === "array") {
				const emptyItem = EMPTY_ARRAY_ITEMS[currentField.key];
				return emptyItem ? [{ ...emptyItem }] : [];
			}
			return "";
		};

		return (
			<Formik
				key={currentField.key}
				initialValues={{ fieldValue: getInitialValue() }}
				validationSchema={getValidationSchema()}
				onSubmit={async (values) => {
					await handleSaveField(values.fieldValue);
				}}
			>
				{(formikProps) => {
					const isSubmitDisabled =
						isSaving ||
						!formikProps.values.fieldValue ||
						(typeof formikProps.values.fieldValue === "string" && !formikProps.values.fieldValue.trim()) ||
						(Array.isArray(formikProps.values.fieldValue) && formikProps.values.fieldValue.length === 0) ||
						!isEmpty(formikProps.errors);

					const footer = showQuitWarning ? (
						<>
							<Button variant="primary" isInline onClick={handleClose}>
								Do not save remaining fields
							</Button>
							<Button variant="secondary" onClick={handleContinueReviewing}>
								Continue reviewing
							</Button>
						</>
					) : editMode !== "none" ? (
						<>
							<Button
								variant="primary"
								isInline
								isLoading={isSaving}
								isDisabled={isSubmitDisabled}
								onClick={() => formikProps.handleSubmit()}
							>
								{pendingFields.length <= 1 ? "Save and complete" : "Save and review next"}
							</Button>
							<Button variant="secondary" onClick={handleSkip} isDisabled={isSaving}>
								Keep existing version
							</Button>
							<Button variant="link" onClick={handleCloseAttempt}>
								Close and do not save remaining fields
							</Button>
						</>
					) : (
						<>
							<Button
								variant="primary"
								onClick={() => {
									formikProps.setFieldValue("fieldValue", currentField.aiValue);
									setEditMode("ai");
								}}
							>
								Accept and edit AI suggestion
							</Button>
							{currentField.hasConflict && (
								<Button
									variant="secondary"
									onClick={() => {
										formikProps.setFieldValue("fieldValue", currentField.existingValue);
										setEditMode("existing");
									}}
								>
									Edit existing version
								</Button>
							)}
							<Button variant="secondary" onClick={handleReject}>
								Reject AI suggestion
							</Button>
							<Button variant="link" isInline onClick={handleCloseAttempt}>
								Close and do not save remaining fields
							</Button>
						</>
					);

					return (
						<Modal
							variant={ModalVariant.large}
							isOpen={isOpen}
							onClose={handleClose}
							appendTo={document.getElementById("modal-div-container") ?? document.body}
							id="ai-import-modal"
							className={`ai-import-modal${showQuitWarning ? "" : " ai-import-modal--reviewing"}`}
							aria-label={MODAL_TITLE}
							header={modalHeader}
							footer={footer}
							tabIndex={0}
						>
							{showQuitWarning ? (
								<Alert
									variant="warning"
									isInline
									title={`You have ${pendingFields.length} generated fields that have not been saved.`}
								>
									If you leave now, the generated suggestions for any unsaved fields will be lost.
								</Alert>
							) : (
								<AIFieldReviewBody
									currentField={currentField}
									totalFields={reviewFields.length}
									pendingCount={pendingFields.length}
									statusMessage={statusMessage}
									formikProps={formikProps}
									onPrevious={handlePrevious}
									onNext={handleNext}
									hasPrevious={findPreviousPending() !== -1}
									hasNext={findNextPending() !== -1}
									editMode={editMode}
									generationId={generationId}
								/>
							)}
						</Modal>
					);
				}}
			</Formik>
		);
	}

	// ─── Complete ────────────────────────────────────────────────────
	if (view === "complete") {
		return (
			<Modal
				variant={ModalVariant.medium}
				isOpen={isOpen}
				onClose={handleClose}
				appendTo={document.getElementById("modal-div-container") ?? document.body}
				id="ai-import-modal"
				className="ai-import-modal"
				aria-label={MODAL_TITLE}
				header={modalHeader}
				actions={[
					<Button key="close" variant="primary" onClick={handleClose}>
						Close
					</Button>,
				]}
			>
				<Alert variant="success" isInline title="All field conflicts have been resolved" />
			</Modal>
		);
	}

	// ─── Form (default) ──────────────────────────────────────────────
	return (
		<Formik
			initialValues={{ urls: submittedUrls.length > 0 ? submittedUrls.join(", ") : "" }}
			validationSchema={urlValidationSchema}
			onSubmit={(values) => {
				handleGenerate(parseUrlEntries(values.urls));
			}}
			enableReinitialize
		>
			{(formikProps) => (
				<Modal
					variant={ModalVariant.medium}
					isOpen={isOpen}
					onClose={handleClose}
					appendTo={document.getElementById("modal-div-container") ?? document.body}
					id="ai-import-modal"
					className="ai-import-modal"
					aria-label={MODAL_TITLE}
					header={modalHeader}
					actions={[
						<Button
							key="generate"
							variant="primary"
							isDisabled={!(formikProps.touched && formikProps.dirty && isEmpty(formikProps.errors))}
							onClick={() => formikProps.handleSubmit()}
						>
							Generate
						</Button>,
						<Button key="cancel" variant="link" onClick={handleClose}>
							Cancel
						</Button>,
					]}
				>
					<Content className="ai-import-modal__description">
						<Content component="p">
							You are about to use a Red Hat tool that utilizes AI technology to provide you with relevant information.
							Do not include any personal information in your input. Responses provided by tools utilizing AI technology
							should be reviewed and verified prior to use. We encourage you to send feedback to improve this feature.
						</Content>
						<Content component="p">
							The product website URL you provide will be used to create AI-generated text that may be refined and saved
							to enhance your product listing. Information may not be up-to-date or without error.
						</Content>
					</Content>
					<Alert
						variant="info"
						isInline={true}
						title="Already completed fields will not be overwritten without confirmation."
						className="ai-import-modal__alert"
					/>
					<FormContainer gridSpan={12}>
						<FormTextInput
							name="urls"
							label="Public product website URL(s)"
							isRequired
							placeholder="https://example.com/product-page"
							helperText="Multiple URLs can be separated by commas."
						/>
					</FormContainer>
				</Modal>
			)}
		</Formik>
	);
};

export default AIImportModal;
